import { LocalDate, LocalDateTime, LocalTime } from 'js-joda';

import { ExperienceTags } from 'components/www/availability-search/filters/SearchFilterExperienceTags';
import { SearchFilterParams } from 'components/search/utils';
import { consumer, core } from '@tock/coretypes';
import { getOfferingTypeQueryParam } from 'utils/searchBar';
import { isExclusivityRestriction } from 'utils/citySearchUtils';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/consumerProto';

const { CollectionPageOfferingAvailabilitySearchRequest, OfferingAvailabilitySearchRequest } =
  protos;

export interface SearchParams extends SearchFilterParams {
  date: LocalDate;
  time: LocalTime;
  size: number;
  type: consumer.OfferingSearchType;
  city?: string;
  q?: string | string[];
  dateEnd?: LocalDate;
  latlng?: string;
  businessType?: string;
  boundingBox?: core.BoundingBox;
}

export interface ServerSearchParams extends Partial<ExperienceTags> {
  dateTime?: {
    min: LocalDateTime;
    max: LocalDateTime;
  };
  size?: {
    min: number;
    max: number;
  };
  location?: consumer.LocationSearchFilter;
  sortType?: consumer.OfferingAvailabilitySearchRequest.SortType;
  type: consumer.OfferingSearchType;
  q?: string | string[];
  userTimeZone?: string;
}

interface SearchOptions {
  additionalQuery?: consumer.IBusinessSearchFilter;
  enableCache?: boolean;
  collectionId?: number;
  collectionDataConfigurationId?: number;
}

export function search(
  serverSearchParams: ServerSearchParams,
  { additionalQuery, enableCache, collectionId, collectionDataConfigurationId }: SearchOptions
): ApiAction {
  const { dateTime, size } = serverSearchParams;
  const postProto =
    collectionId || collectionDataConfigurationId
      ? `/v2/consumer/search/collection`
      : '/v2/consumer/search/business';

  const baseSearchRequest = convertToOfferingAvailabilitySearchRequestProto(
    serverSearchParams,
    additionalQuery
  );
  const collectionPageRequest =
    collectionId || collectionDataConfigurationId
      ? getCollectionPageRequestFromRequest(baseSearchRequest, {
          collectionId,
          collectionDataConfigurationId,
        })
      : undefined;

  // If we have a collectionId, and thus collectionPageRequest is not undefined, send that request proto
  const request = collectionPageRequest ?? baseSearchRequest;

  const cacheOptions = enableCache
    ? {
        enableCache: true,
        maxAge: 60 * 1000,
      }
    : undefined;

  return {
    TockApi: {
      postProto,
      request,
      type: AppConstants.AVAILABILITY_SEARCH,
      context: {
        date: dateTime ? dateTime.min.toLocalDate().toString() : undefined,
        time: dateTime ? dateTime.min.toLocalTime().toString() : undefined,
        size,
        additionalQuery,
      },
      cacheOptions,
    },
  };
}

export function clear(): BaseTockAction {
  return {
    type: AppConstants.AVAILABILITY_SEARCH_CLEAR,
  };
}

export function setShouldScrollToTopOnNewResults(value: boolean): BaseTockAction {
  return {
    type: AppConstants.AVAILABILITY_SET_SCROLL_TO_TOP,
    payload: value,
  };
}

const getExperienceMetaTagsFromQuery = (
  tags: string[] | undefined,
  type: core.MetaTag.TagType
): core.IMetaTag[] => {
  return tags?.map((tag) => ({ name: tag, type })) || [];
};

// exported for testing
export function convertToOfferingAvailabilitySearchRequestProto(
  {
    dateTime,
    size,
    type: userSuppliedType,
    q,
    userTimeZone,
    location,
    sortType,
    experienceTagAll,
    experienceTagDineIn,
    experienceTagToGo,
    experienceTagCampaign,
    experienceTagSeatingArea,
  }: ServerSearchParams,
  additionalQuery?: consumer.IBusinessSearchFilter
): consumer.IOfferingAvailabilitySearchRequest {
  let type: consumer.OfferingSearchType;
  let exclusivityRestriction: consumer.ExclusivityRestriction[];
  if (isExclusivityRestriction(userSuppliedType)) {
    type = 'ALL_EXPERIENCES';
    exclusivityRestriction = [userSuppliedType];
  } else {
    type = userSuppliedType;
    exclusivityRestriction = [];
  }

  const request: consumer.IOfferingAvailabilitySearchRequest = {
    dateTime: dateTime
      ? {
          min: dateTime.min.toString(),
          max: dateTime.max.toString(),
        }
      : undefined,
    offering: {
      type: getOfferingTypeQueryParam(type),
      exclusivityRestriction,
      ticketTypeTag: [
        ...getExperienceMetaTagsFromQuery(experienceTagAll, 'EXPERIENCE_ALL'),
        ...getExperienceMetaTagsFromQuery(experienceTagDineIn, 'EXPERIENCE_IN_PERSON'),
        ...getExperienceMetaTagsFromQuery(experienceTagToGo, 'EXPERIENCE_TO_GO'),
        ...getExperienceMetaTagsFromQuery(experienceTagCampaign, 'CAMPAIGN'),
        ...getExperienceMetaTagsFromQuery(experienceTagSeatingArea, 'SEATING_AREA'),
      ],
    },
    location,
    sortType,
    partySize: size,
    business: additionalQuery,
    numResults: 500,
  };

  if (q) {
    request.matchingString = Array.isArray(q) ? q : [q];
  }

  if (userTimeZone) {
    request.userTimeZone = userTimeZone;
  }

  return OfferingAvailabilitySearchRequest.fromObject(request);
}

interface CollectionPageRequestParams {
  collectionId: consumer.ICollectionPageOfferingAvailabilitySearchRequest['collectionId'];
  collectionDataConfigurationId: consumer.ICollectionPageOfferingAvailabilitySearchRequest['collectionDataConfigurationId'];
}

/**
 * Generates a wrapper proto, containing a base OfferingAvailabilitySearchRequest, and
 * any collection-page-specific params (e.g., collectionId)
 */
export function getCollectionPageRequestFromRequest(
  baseSearchRequest: consumer.IOfferingAvailabilitySearchRequest,
  collectionParams: CollectionPageRequestParams
): consumer.ICollectionPageOfferingAvailabilitySearchRequest {
  const collectionPageRequest: consumer.ICollectionPageOfferingAvailabilitySearchRequest = {
    baseSearchRequest,
    ...collectionParams,
  };

  return CollectionPageOfferingAvailabilitySearchRequest.fromObject(collectionPageRequest);
}

export function clearSsrInitialization(): BaseTockAction {
  return {
    type: AppConstants.AVAILABILITY_CLEAR_SSR_INITIALIZATION,
  };
}

export function resetSsrInitialization(): BaseTockAction {
  return {
    type: AppConstants.AVAILABILITY_RESET_SSR_INITIALIZATION,
  };
}
